import { render, h, Fragment } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { processPattern } from './pattern-processer';
import { css } from '@emotion/react';
import { LightIntlProvider } from '@/lib/i18n/i18n-provider';
import { resources as i18nResources } from '@/lib/i18n';
import { getDetectedLanguage } from './utils/detect-language';
import { CacheProvider } from '@emotion/react';
import { toastIframeManager } from './utils/singleton-iframe';

interface ToastProps {
  message: string | preact.VNode;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose?: () => void;
  position?: 'top' | 'center';
  customStyle?: React.CSSProperties;
}

const Toast = ({
  message,
  type = 'info',
  duration = 3000,
  onClose,
  position = 'top',
  customStyle
}: ToastProps) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        onClose?.();
      }, 300); // Wait for fade-out animation to complete
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const top = useMemo(() => {
    const statusBar = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--status-bar-height') || '0',
      10
    ); // add 12px for android navigation bar height

    return Math.max(20, statusBar);
  }, []);

  const toastStyles = css`
    position: fixed;
    left: 50%;
    padding: 10px 16px;
    max-width: min(85vw, 400px);
    // min-width: 200px;
    width: auto;
    font-family: PingFangSC-Regular;
    background: ${type === 'success'
      ? '#4caf50'
      : type === 'error'
      ? '#f44336'
      : type === 'warning'
      ? '#ff9800'
      : '#2F2F3B'};
    color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    z-index: 9999;
    transition: opacity 0.3s, transform 0.3s;
    text-align: center;
    word-wrap: break-word;
    display: flex;
    align-items: center;
    justify-content: center;

    /* Base styles for hidden state */
    opacity: 0;

    /* Top position styles */
    ${position === 'top' &&
    `
      top: max(${top}px, env(safe-area-inset-top));
      transform: translate(-50%, -20px);
    `}

    /* Center position styles */
    ${position === 'center' &&
    `
      top: 50%;
      transform: translate(-50%, -50%) scale(0.9);
    `}

    /* Visible state styles */
    ${isVisible &&
    position === 'top' &&
    `
      opacity: 1;
      transform: translate(-50%, 0);
    `}

    ${isVisible &&
    position === 'center' &&
    `
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    `}

    ${customStyle &&
    `
      ${Object.entries(customStyle)
        .map(([key, value]) => `${key}: ${value};`)
        .join('\n')}
    `}
  `;

  return (
    <div className={`toast toast-${type}`} style={toastStyles.styles}>
      {message}
    </div>
  );
};

export interface IToastOptions {
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  checkmarkImage?: string;
  coinImage?: string;
  gemImage?: string;
  customStyle?: {
    mark?: React.CSSProperties;
    coin?: React.CSSProperties;
    gem?: React.CSSProperties;
    base?: React.CSSProperties;
    correct?: React.CSSProperties;
    span?: React.CSSProperties;
  };
  i18nParams?: Record<string, string | number>;
  position?: 'top' | 'center';
}

const statusBar = parseInt(
  getComputedStyle(document.documentElement).getPropertyValue('--status-bar-height') || '0',
  10
);
const TOAST_MIN_TOP = Math.max(20, statusBar);
const TOAST_GAP = 12; // px
const activeToastsTop: HTMLElement[] = [];
const activeToastsCenter: HTMLElement[] = [];

function layoutToasts(position: 'top' | 'center') {
  if (position === 'top') {
    let offset = 0;
    activeToastsTop.forEach((el, idx) => {
      const targetEl = el.querySelector('.toast') as HTMLElement;
      if (!targetEl) return;
      if (idx === 0) {
        targetEl.style.top = `max(${TOAST_MIN_TOP}px, env(safe-area-inset-top))`;
      } else {
        targetEl.style.top = `calc(max(${TOAST_MIN_TOP}px, env(safe-area-inset-top)) + ${offset}px)`;
      }

      offset += targetEl.offsetHeight + TOAST_GAP;
    });
  } else if (position === 'center') {
    let offset = 0;
    activeToastsCenter.forEach((el) => {
      const targetEl = el.querySelector('.toast') as HTMLElement;
      if (!targetEl) return;
      targetEl.style.top = `calc(50% + ${offset}px)`;
      offset += targetEl.offsetHeight + TOAST_GAP;
    });
  }
}

export const createToast = async (pattern: string, options?: IToastOptions) => {
  const { iframe, emotionCache } = await toastIframeManager.acquire();
  const doc = iframe.contentDocument;
  if (!doc) return;

  const toastContainer = doc.createElement('div');
  doc.body.appendChild(toastContainer);
  const position = options?.position || 'top';
  const queue = position === 'top' ? activeToastsTop : activeToastsCenter;
  queue.push(toastContainer);

  const processedMessage = processPattern(pattern, options);
  const language = getDetectedLanguage();
  const currentLanguage = i18nResources[language as keyof typeof i18nResources].translations;

  function handleClose() {
    doc?.body.removeChild(toastContainer);
    const idx = queue.indexOf(toastContainer);
    if (idx !== -1) queue.splice(idx, 1);
    layoutToasts(position);
    toastIframeManager.release();
  }

  setTimeout(() => layoutToasts(position), 0);

  render(
    <CacheProvider value={emotionCache}>
      <LightIntlProvider definition={currentLanguage}>
        <Toast
          message={processedMessage}
          type={options?.type}
          position={position}
          duration={options?.duration}
          onClose={handleClose}
          customStyle={options?.customStyle?.base}
        />
      </LightIntlProvider>
    </CacheProvider>,
    toastContainer
  );
};
